/**
 * FlawDesk-API-Hooks fuer das Outlook Add-in.
 *
 * Diese Hooks setzen voraus, dass useAuth() in status='ready' ist.
 * Solange MSAL noch nicht durch ist, halten sich die Hooks zurueck —
 * sonst wuerden sie 401 returnen, weil acquireToken() noch keine
 * Session hat.
 */

import { useEffect, useState } from 'react'
import { apiFetch, ApiError } from './client.js'

/** Lifecycle-Stati fuer einen Datenabruf. */
const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
}

/**
 * Holt die Workspaces des Users (Membership).
 */
export function useWorkspaces(auth) {
  const [state, setState] = useState({ status: STATUS.IDLE })

  useEffect(() => {
    if (auth.status !== 'ready') {
      setState({ status: STATUS.IDLE })
      return
    }
    let cancelled = false
    setState({ status: STATUS.LOADING })

    apiFetch('/api/workspaces')
      .then((data) => {
        if (cancelled) return
        const list = Array.isArray(data) ? data : []
        setState({ status: STATUS.READY, workspaces: list })
      })
      .catch((err) => {
        if (cancelled) return
        setState({ status: STATUS.ERROR, error: err })
      })

    return () => { cancelled = true }
  }, [auth.status, auth.account?.username])

  return state
}

/**
 * Holt die Projekte eines Workspace. Nur aktiv wenn workspaceId gesetzt.
 */
export function useProjects(auth, workspaceId) {
  const [state, setState] = useState({ status: STATUS.IDLE })

  useEffect(() => {
    if (auth.status !== 'ready' || !workspaceId) {
      setState({ status: STATUS.IDLE })
      return
    }
    let cancelled = false
    setState({ status: STATUS.LOADING })

    apiFetch(`/api/projects?workspace_id=${encodeURIComponent(workspaceId)}`)
      .then((data) => {
        if (cancelled) return
        const list = Array.isArray(data) ? data : []
        setState({ status: STATUS.READY, projects: list })
      })
      .catch((err) => {
        if (cancelled) return
        setState({ status: STATUS.ERROR, error: err })
      })

    return () => { cancelled = true }
  }, [auth.status, auth.account?.username, workspaceId])

  return state
}

/**
 * Erstellt ein Ticket. Wirft ApiError bei Fehlern.
 *
 * @param {object} payload — { title, description, workspace_id, project_id?, priority? }
 * @returns {Promise<object>} — { id, nr, title, ... }
 */
export async function createTask(payload) {
  // Backend erwartet project_id (key) und workspace_id getrennt
  const body = {
    title: payload.title,
    description: payload.description || '',
    workspace_id: payload.workspace_id,
    source: 'outlook',
  }
  if (payload.project_id) body.project_id = payload.project_id
  if (payload.priority) body.priority = payload.priority
  if (payload.platform) body.platform = payload.platform
  if (payload.tags) body.tags = payload.tags
  // #1088: Mail-Thread mitschicken wenn vorhanden. Backend persistiert in
  // task_mail_messages. Wenn das Array fehlt, faellt das Backend auf den
  // alten Verhalten zurueck (nur description wird genutzt).
  if (payload.mail_thread && Array.isArray(payload.mail_thread) && payload.mail_thread.length > 0) {
    body.mail_thread = payload.mail_thread
  }

  return apiFetch('/api/tasks', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Holt das FlawDesk-User-Profil zur Auth-Identitaet.
 *
 * @param {object} auth — Rueckgabe von useAuth(). Erst aktiv wenn status='ready'.
 * @returns {{ status, user?, error? }}
 */
export function useFlawDeskMe(auth) {
  const [state, setState] = useState({ status: STATUS.IDLE })

  useEffect(() => {
    if (auth.status !== 'ready') {
      setState({ status: STATUS.IDLE })
      return
    }

    let cancelled = false
    setState({ status: STATUS.LOADING })

    apiFetch('/api/auth/me')
      .then((data) => {
        if (cancelled) return
        // Backend antwortet mit { user: {...} }
        const user = data?.user ?? null
        if (!user) {
          setState({
            status: STATUS.ERROR,
            error: new ApiError('malformed_response', 'Antwort enthielt kein user-Objekt'),
          })
          return
        }
        setState({ status: STATUS.READY, user })
      })
      .catch((err) => {
        if (cancelled) return
        setState({ status: STATUS.ERROR, error: err })
      })

    return () => {
      cancelled = true
    }
  }, [auth.status, auth.account?.username])

  return state
}

/**
 * Holt die offenen Tickets des eingeloggten Users (assignee_id = self).
 *
 * @param {object} auth — Rueckgabe von useAuth(). Erst aktiv wenn status='ready'.
 * @param {object=} options — { limit?: number, workspaceId?: string }
 * @returns {{ status, tickets?, error? }}
 */
export function useMyTickets(auth, options = {}) {
  const [state, setState] = useState({ status: STATUS.IDLE })
  const limit = options.limit ?? 20
  const workspaceId = options.workspaceId

  useEffect(() => {
    if (auth.status !== 'ready') {
      setState({ status: STATUS.IDLE })
      return
    }

    let cancelled = false
    setState({ status: STATUS.LOADING })

    const params = new URLSearchParams({
      assigned_to_me: '1',
      limit: String(limit),
    })
    if (workspaceId) {
      params.set('workspace_id', workspaceId)
    }

    apiFetch(`/api/tasks?${params.toString()}`)
      .then((data) => {
        if (cancelled) return
        // Backend antwortet mit Array von Tasks
        const tickets = Array.isArray(data) ? data : []
        // Done-Tickets ausblenden, und auch erledigte (status=fixed/rejected)
        // — der Add-in-Use-Case ist "was ist noch zu tun".
        const inactiveStatuses = ['fixed', 'rejected', 'closed']
        const openTickets = tickets
          .filter((t) => !t.done)
          .filter((t) => !inactiveStatuses.includes(t.status))
          .sort((a, b) => {
            const ad = a.updated_at || a.created_at || ''
            const bd = b.updated_at || b.created_at || ''
            return bd.localeCompare(ad)
          })
        setState({ status: STATUS.READY, tickets: openTickets })
      })
      .catch((err) => {
        if (cancelled) return
        setState({ status: STATUS.ERROR, error: err })
      })

    return () => {
      cancelled = true
    }
  }, [auth.status, auth.account?.username, limit, workspaceId])

  return state
}

/**
 * #1088: Tickets finden, die mit der aktuell offenen Mail oder ihrem Thread
 * verknuepft sind. Eingabe: internetMessageId + conversationId. Beide werden
 * an /api/tasks/by-mail uebergeben — Backend liefert dedupliziert eine Liste
 * mit `match: 'exact' | 'thread'`.
 *
 * Refetch-Trigger: Mail-Wechsel (anderer Outlook-Item → andere IDs).
 * Damit aktualisiert sich die Anzeige automatisch wenn der User durch
 * seine Mails navigiert.
 *
 * Returns: { status, tasks?, error? }
 */
export function useTasksForMail(auth, { internetMessageId, conversationId } = {}) {
  const [state, setState] = useState({ status: STATUS.IDLE })

  useEffect(() => {
    if (auth.status !== 'ready') {
      setState({ status: STATUS.IDLE })
      return
    }
    if (!internetMessageId && !conversationId) {
      setState({ status: STATUS.READY, tasks: [] })
      return
    }

    let cancelled = false
    setState({ status: STATUS.LOADING })

    const params = new URLSearchParams()
    if (internetMessageId) params.set('internet_message_id', internetMessageId)
    if (conversationId)    params.set('conversation_id', conversationId)

    apiFetch(`/api/tasks/by-mail?${params.toString()}`)
      .then((data) => {
        if (cancelled) return
        const tasks = Array.isArray(data?.tasks) ? data.tasks : []
        setState({ status: STATUS.READY, tasks })
      })
      .catch((err) => {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.warn('[#1088] useTasksForMail failed:', err)
        // Stille Degradation: kein Banner, kein Fehler-Toast. Die Info
        // "es gibt vielleicht schon Tickets" ist nice-to-have, kein
        // Showstopper.
        setState({ status: STATUS.READY, tasks: [] })
      })

    return () => { cancelled = true }
  }, [auth.status, auth.account?.username, internetMessageId, conversationId])

  return state
}
